'use client';

import React, { useState } from 'react';

export interface CardProps {
  /** Cover image URL rendered above the body. */
  media?: string | null;
  /** CSS aspect-ratio for the media (default 3 / 2; portfolio tiles 4 / 5). */
  mediaRatio?: string;
  /** Body padding in px. Default 16. */
  padding?: number;
  /** Adds hover lift + stronger shadow. */
  interactive?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * Surface container. `media` renders a cover image at the top (4:5 tiles,
 * 3:2 covers per imagery rules). `interactive` adds hover lift.
 */
export function Card({
  media = null,
  mediaRatio = '3 / 2',
  padding = 16,
  interactive = false,
  onClick,
  children,
  style = {},
  ...rest
}: CardProps & Omit<React.HTMLAttributes<HTMLDivElement>, keyof CardProps>) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
        boxShadow: interactive && hover ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: interactive && hover ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow var(--dur-standard), transform var(--dur-standard)',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
      {...rest}
    >
      {media && (
        <div
          style={{
            width: '100%',
            aspectRatio: mediaRatio,
            backgroundImage: `url(${media})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <div style={{ padding }}>{children}</div>
    </div>
  );
}
